/**
 * Rol bazlı hazır PAKETLER + merkezi à-la-carte fiyat hesabı.
 * Katalog tek kaynak: [[uygulama-katalog]] (uygulama+alt özellik) + [[moduller]] (3D workbench+komut).
 * Sihirbaz (public + admin) ve lisans ekranları buradan beslenir — parent kilidi TEK yerde uygulanır.
 */
import { UYGULAMA_KATALOG, ALT_OZ_ANAHTAR, IUygulamaTanim } from './uygulama-katalog';
import { MODULLER, KOMUT_ANAHTAR, ModulId } from './moduller';

/** Bir kullanıcının/cihazın à-la-carte seçimi (entitlement set'lerinin dizi karşılığı). */
export interface ISecim {
  uyg: string[];   // açık uygulama id'leri (emoji)
  alt: string[];   // "uygId:altId"
  wb: string[];    // açık 🌌 workbench id'leri
  km: string[];    // "wbId:komutId"
}

/** Bir kalemin erişim seviyesi (kampanya/geçiş dönemi dahil). */
export type ErisimSeviye = 'ucretli' | 'uye' | 'herkes' | 'kampanya';
export interface IErisimAyar { seviye: ErisimSeviye; bitis?: string; }   // anahtar → ayar
export const SEVIYE_BILGI: Record<ErisimSeviye, { ad: string; kisa: string; renk: string; bedavaSimdi: boolean }> = {
  ucretli:  { ad: 'Ücretli',          kisa: '',                 renk: '#d97706', bedavaSimdi: false },
  uye:      { ad: 'Üyelere Ücretsiz', kisa: 'Üyelere ücretsiz', renk: '#0ea5e9', bedavaSimdi: true },
  herkes:   { ad: 'Herkese Ücretsiz', kisa: 'Ücretsiz',         renk: '#16a34a', bedavaSimdi: true },
  kampanya: { ad: 'Kampanya',         kisa: 'Şimdilik ücretsiz',renk: '#a855f7', bedavaSimdi: true },
};
/** Kampanya bitti mi? (bitiş geçmişse artık ücretli sayılır.) */
export function etkinSeviye(ayar?: IErisimAyar, suAnIso?: string): ErisimSeviye {
  if (!ayar) return 'ucretli';
  if (ayar.seviye === 'kampanya' && ayar.bitis && suAnIso && ayar.bitis < suAnIso) return 'ucretli';
  return ayar.seviye;
}
/** Bir seviye şu an bedava mı? (uye → girişli kullanıcıya bedava; çağıran login durumuyla birleştirir.) */
export function seviyeBedavaMi(seviye: ErisimSeviye): boolean { return SEVIYE_BILGI[seviye]?.bedavaSimdi === true; }

export interface IFiyatKalem { ad: string; fiyat: number; ust?: boolean; bedava?: boolean; normalFiyat?: number; seviye?: ErisimSeviye; }
export interface IFiyatSonuc { kalemler: IFiyatKalem[]; araToplam: number; }

/** Rol bazlı hazır paket — katalogdan bir alt küme + indirim. */
export interface IPaket {
  id: string;
  ad: string;
  simge: string;
  renk: string;
  aciklama: string;
  rozet?: string;          // "EN POPÜLER" vb.
  indirimYuzde: number;    // tek-tek toplamdan düşülen %
  secim: ISecim;
  fiyatlar?: Record<string, number>; // PAKETE ÖZEL kalem fiyatları ("anahtar"→₺); global'i ezer
  _id?: string;            // DB kaydı (admin tanımlı paketlerde)
  aktif?: boolean;         // public'te görünür mü (varsayılan true)
  sira?: number;           // sıralama
}

export const BOS_SECIM = (): ISecim => ({ uyg: [], alt: [], wb: [], km: [] });

/** Tüm katalog (Tam Sürüm paketi + referans fiyat için). */
export function tumKatalogSecim(): ISecim {
  const s = BOS_SECIM();
  for (const u of UYGULAMA_KATALOG) {
    if (u.mod !== 'ucretli') continue;
    s.uyg.push(u.id);
    for (const a of u.altOzellikler || []) s.alt.push(ALT_OZ_ANAHTAR(u.id, a.id));
  }
  s.uyg.push('🌌');
  for (const w of MODULLER) {
    s.wb.push(w.id);
    for (const k of w.komutlar || []) s.km.push(KOMUT_ANAHTAR(w.id, k.id));
  }
  s.uyg = [...new Set(s.uyg)];
  return s;
}

/** Workbench + seçili komut id'leriyle hızlı seçim parçası. */
const wbSec = (id: ModulId, komutlar: string[] = []): { wb: string[]; km: string[] } =>
  ({ wb: [id], km: komutlar.map(k => KOMUT_ANAHTAR(id, k)) });

const birlestir = (...parcalar: Array<Partial<ISecim>>): ISecim => {
  const s = BOS_SECIM();
  for (const p of parcalar) {
    s.uyg.push(...(p.uyg || [])); s.alt.push(...(p.alt || []));
    s.wb.push(...(p.wb || [])); s.km.push(...(p.km || []));
  }
  return { uyg: [...new Set(s.uyg)], alt: [...new Set(s.alt)], wb: [...new Set(s.wb)], km: [...new Set(s.km)] };
};

// ──────────────────────────────────────────────────────────────
// ROL BAZLI PAKETLER (kullanıma göre)
// ──────────────────────────────────────────────────────────────
export const ROL_PAKETLERI: IPaket[] = [
  {
    id: 'satinalmaci', ad: 'Satınalmacı', simge: '🛒', renk: '#0ea5e9', indirimYuzde: 15,
    aciklama: 'STP/3D modeli aç, ölç, kontrol et. Teklif ve sipariş öncesi hızlı doğrulama.',
    secim: birlestir(
      { uyg: ['🌌'] },
      wbSec('GORUNTULEME', ['kesit_gorunum']),
      wbSec('OLCUM', ['kumpas', 'cetvel', 'cap', 'agirlik']),
    ),
  },
  {
    id: 'muhendis', ad: 'Mühendis', simge: '📐', renk: '#6366f1', indirimYuzde: 20, rozet: 'EN POPÜLER',
    aciklama: 'Tam ölçüm + DFM/döküm analizi + 2D/3D çizim ve teknik resim.',
    secim: birlestir(
      { uyg: ['🌌'] },
      wbSec('GORUNTULEME', ['patlatma', 'xray', 'kesit_gorunum']),
      wbSec('OLCUM', ['kumpas', 'cetvel', 'aci', 'cap', 'min_mesafe', 'alan_hacim', 'agirlik', 'gdt']),
      wbSec('ANALIZ', ['dokum', 'ters_aci', 'kalinlik', 'radius', 'dokum_kontrol']),
      wbSec('CIZIM', ['sketch', 'parametrik', 'cizgi_3d', 'teknik_resim']),
    ),
  },
  {
    id: 'uretim', ad: 'Atölye / Üretim', simge: '🏭', renk: '#f59e0b', indirimYuzde: 22,
    aciklama: 'CAM + açılı kafa + Strafor kesim + HMI atölye panosu. Tezgâha kadar üretim.',
    secim: birlestir(
      { uyg: ['🌌'] },
      wbSec('GORUNTULEME', ['kesit_gorunum']),
      wbSec('OLCUM', ['kumpas', 'cetvel', 'cap', 'agirlik']),
      wbSec('CAM', ['cam_2d', 'bes_eksen', 'simulasyon', 'nc_post']),
      wbSec('ACILI_KAFA', ['ozmo', 'diagonal']),
      { uyg: ['✂️'], alt: [ALT_OZ_ANAHTAR('✂️', 'nesting'), ALT_OZ_ANAHTAR('✂️', 'gcode'), ALT_OZ_ANAHTAR('✂️', 'simulasyon')] },
      { uyg: ['📟'], alt: [ALT_OZ_ANAHTAR('📟', 'dashboard'), ALT_OZ_ANAHTAR('📟', 'pdks'), ALT_OZ_ANAHTAR('📟', 'uretim')] },
    ),
  },
  {
    id: 'tam', ad: 'Tam Sürüm', simge: '💎', renk: '#a855f7', indirimYuzde: 65,
    aciklama: 'Tüm uygulamalar, tüm workbench ve komutlar + Parça Takip. Kurumsal her şey dahil.',
    secim: tumKatalogSecim(),
  },
];

export const PAKET_BUL = (id: string): IPaket | undefined => ROL_PAKETLERI.find(p => p.id === id);

// ──────────────────────────────────────────────────────────────
// MERKEZİ FİYAT HESABI — parent kilidi BURADA (tek kaynak)
// ──────────────────────────────────────────────────────────────
/**
 * Seçimden aylık fiyat kalemleri. KURAL: parent erişim kapalıysa altları ücretlendirilmez.
 *  - Bir uygulama kapalıysa → alt özellikleri sayılmaz.
 *  - 🌌 3D erişimi kapalıysa → tüm workbench/komutlar sayılmaz.
 * `override`: "uygId" / "uygId:altId" / "wbId" / "wbId:komutId" → kullanıcıya özel fiyat.
 */
export function fiyatHesapla(secim: Partial<ISecim>, override: Record<string, number> = {}, seviyeler: Record<string, IErisimAyar> = {}): IFiyatSonuc {
  const uyg = new Set(secim.uyg || []), alt = new Set(secim.alt || []);
  const wb = new Set(secim.wb || []), km = new Set(secim.km || []);
  const fy = (k: string, v: number) => (override[k] != null ? override[k] : v);
  const suAn = new Date().toISOString();
  /** Kalemi seviye'ye göre üret: bedava ise fiyat 0 + normalFiyat (geçiş/kampanya gösterimi). */
  const kalem = (anahtar: string, ad: string, normal: number, ust?: boolean): IFiyatKalem => {
    const sv = etkinSeviye(seviyeler[anahtar], suAn);
    if (seviyeBedavaMi(sv)) return { ad, fiyat: 0, ust, bedava: true, normalFiyat: normal, seviye: sv };
    return { ad, fiyat: normal, ust, seviye: 'ucretli' };
  };
  const kalemler: IFiyatKalem[] = [];

  for (const u of UYGULAMA_KATALOG) {
    if (u.mod !== 'ucretli' || !uyg.has(u.id)) continue;
    kalemler.push(kalem(u.id, `${u.simge} ${u.baslik}`, fy(u.id, u.aylikFiyat || 0), true));
    for (const a of u.altOzellikler || [])
      if (alt.has(ALT_OZ_ANAHTAR(u.id, a.id)))
        kalemler.push(kalem(ALT_OZ_ANAHTAR(u.id, a.id), `＋ ${a.baslik}`, fy(ALT_OZ_ANAHTAR(u.id, a.id), a.ekFiyat)));
  }

  if (uyg.has('🌌')) {
    for (const w of MODULLER) {
      if (!wb.has(w.id)) continue;
      kalemler.push(kalem(w.id, `${w.simge} ${w.baslik}`, fy(w.id, w.aylikFiyat), true));
      for (const k of w.komutlar || [])
        if (km.has(KOMUT_ANAHTAR(w.id, k.id)))
          kalemler.push(kalem(KOMUT_ANAHTAR(w.id, k.id), `＋ ${k.baslik}`, fy(KOMUT_ANAHTAR(w.id, k.id), k.ekFiyat)));
    }
  }

  return { kalemler, araToplam: kalemler.reduce((t, k) => t + k.fiyat, 0) };
}

/** Paket fiyatı: tek-tek toplam (ara) ve indirimli net + kampanyasız "normal" (üstü çizili gösterim). */
export function paketFiyat(p: IPaket, disOverride: Record<string, number> = {}, seviyeler: Record<string, IErisimAyar> = {}): { ara: number; net: number; indirim: number; normal: number } {
  const override = { ...disOverride, ...(p.fiyatlar || {}) };
  const ara = fiyatHesapla(p.secim, override, seviyeler).araToplam;   // kampanya/ücretsiz uygulanmış
  const normal = fiyatHesapla(p.secim, override).araToplam;           // seviyesiz GERÇEK fiyat (üstü çizili)
  const indirim = Math.round((ara * p.indirimYuzde) / 100);
  return { ara, net: ara - indirim, indirim, normal };
}

/** Paket içinde şu an KAMPANYALI kalem var mı? (üstü çizili + 🎁 rozet için.) */
export function paketKampanyaliMi(p: IPaket, seviyeler: Record<string, IErisimAyar> = {}): boolean {
  const suAn = new Date().toISOString();
  const tum = [...p.secim.uyg, ...p.secim.alt, ...p.secim.wb, ...p.secim.km];
  return tum.some(a => etkinSeviye(seviyeler[a], suAn) === 'kampanya');
}

/** Tüm ücretlendirilebilir kalemler — gruplu (global fiyat ayarları ekranı + genel kullanım için). */
export interface IFiyatKalemTanim { anahtar: string; baslik: string; kodFiyat: number; }
export interface IFiyatGrup { baslik: string; simge: string; kalemler: IFiyatKalemTanim[]; }
export function katalogFiyatKalemleri(): IFiyatGrup[] {
  const gruplar: IFiyatGrup[] = [];
  for (const u of UYGULAMA_KATALOG) {
    if (u.mod !== 'ucretli') continue;
    const kalemler: IFiyatKalemTanim[] = [{ anahtar: u.id, baslik: `${u.baslik} (baz erişim)`, kodFiyat: u.aylikFiyat || 0 }];
    for (const a of u.altOzellikler || []) kalemler.push({ anahtar: ALT_OZ_ANAHTAR(u.id, a.id), baslik: a.baslik, kodFiyat: a.ekFiyat });
    gruplar.push({ baslik: u.baslik, simge: u.simge, kalemler });
  }
  for (const w of MODULLER) {
    const kalemler: IFiyatKalemTanim[] = [{ anahtar: w.id, baslik: `${w.baslik} (baz workbench)`, kodFiyat: w.aylikFiyat }];
    for (const k of w.komutlar || []) kalemler.push({ anahtar: KOMUT_ANAHTAR(w.id, k.id), baslik: k.baslik, kodFiyat: k.ekFiyat });
    gruplar.push({ baslik: `🌌 ${w.baslik}`, simge: w.simge, kalemler });
  }
  return gruplar;
}

/** Bir uygulamanın altında alt özellik VEYA (🌌 ise) workbench olup olmadığı — detay adımı için. */
export function detayliMi(u: IUygulamaTanim): boolean {
  return u.id === '🌌' || (u.altOzellikler?.length || 0) > 0;
}
